import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Avatar, Box, Divider, Icon, IconButton, List, Tooltip, Typography } from "@mui/material";
import { useMaterialUIController } from "app/providers";
import { useAuth } from "app/providers/authContext";
import LogoutButton from "app/components/LogoutButton";
import miniLogo from "assets/images/logopass.png";
import mobileBrand from "assets/images/logoBlanco.png";
import SidenavCollapse from "./SidenavCollapse";
import SidenavRoot from "./SidenavRoot";
import useSidenav from "./useSidenav";
import { filterNavigation, isBranchActive, isRouteActive } from "./navigationUtils";

function Sidenav({ brand = "", brandName, routes }) {
  const { user, hasPermission } = useAuth();
  const [controller] = useMaterialUIController();
  const { transparentSidenav, whiteSidenav, darkMode, direction } = controller;
  const { isDesktop, isMini, mobileOpen, toggle, closeMobile } = useSidenav();
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    closeMobile();
  }, [pathname, search, hash, isDesktop, closeMobile]);
  const filteredRoutes = useMemo(
    () => filterNavigation(routes, hasPermission),
    [routes, hasPermission]
  );
  const displayUser = user?.user || user;
  const userName =
    [displayUser?.nombre, displayUser?.name, displayUser?.username]
      .find((value) => typeof value === "string" && value.trim())
      ?.trim() || "Usuario";
  const role = displayUser?.role;
  const currentUserRole = typeof role === "string" ? role : role?.name || "Usuario";
  const names = userName.split(/\s+/);
  const initials = (
    names.length > 1 ? names[0][0] + names[1][0] : userName.slice(0, 2)
  ).toUpperCase();
  const isLight = isDesktop && (whiteSidenav || (transparentSidenav && !darkMode));
  const textColor = isLight ? "#344767" : "#fff";

  const renderItem = (item, level = 0, parent = "") => {
    const key = `${parent}/${item.key}`;
    if (item.type === "divider")
      return isMini ? null : (
        <Divider key={key} sx={{ borderColor: textColor, opacity: 0.15, my: 1.5 }} />
      );
    if (item.type === "title")
      return isMini ? null : (
        <Typography key={key} variant="caption" sx={{ px: 2, color: textColor, opacity: 0.6 }}>
          {item.title}
        </Typography>
      );
    if (item.type !== "collapse") return null;
    return (
      <SidenavCollapse
        key={key}
        name={item.name}
        icon={item.icon}
        route={item.route === "#" ? null : item.route}
        href={item.href}
        active={isRouteActive(item, pathname)}
        branchActive={isBranchActive(item, pathname)}
        level={level}
        isMini={isMini}
        isMobile={!isDesktop}
        onNavigate={closeMobile}
        onExpandMenu={toggle}
        collapse={
          item.collapse?.length
            ? item.collapse.map((child) => renderItem(child, level + 1, key))
            : null
        }
      />
    );
  };
  return (
    <SidenavRoot
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop || mobileOpen}
      onClose={closeMobile}
      anchor={direction === "rtl" ? "right" : "left"}
      PaperProps={{ id: "goper-sidenav", component: "nav", "aria-label": "Menú principal" }}
      ownerState={{ isDesktop, isMini, transparentSidenav, whiteSidenav, darkMode, direction }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minHeight: 72,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={isMini ? miniLogo : !isDesktop ? mobileBrand : brand || miniLogo}
          alt={brandName || "Goper"}
          sx={{
            width: isMini ? 32 : 128,
            maxWidth: isMini ? "100%" : "calc(100% - 64px)",
            maxHeight: 48,
            objectFit: "contain",
          }}
        />
        {!isMini && (
          <Tooltip title={isDesktop ? "Colapsar menú" : "Cerrar menú"}>
            <IconButton
              onClick={toggle}
              aria-label={isDesktop ? "Colapsar menú" : "Cerrar menú"}
              sx={{
                color: textColor,
                position: "absolute",
                insetInlineEnd: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 32,
                height: 32,
              }}
            >
              <Icon>
                {isDesktop ? (direction === "rtl" ? "chevron_right" : "chevron_left") : "close"}
              </Icon>
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isMini && (
        <Tooltip title="Expandir menú" placement="right">
          <IconButton
            onClick={toggle}
            aria-label="Expandir menú"
            sx={{ color: textColor, mx: "auto", mb: 1 }}
          >
            <Icon>{direction === "rtl" ? "chevron_left" : "chevron_right"}</Icon>
          </IconButton>
        </Tooltip>
      )}
      <Divider sx={{ mx: 2, borderColor: textColor, opacity: 0.15 }} />
      <Box
        tabIndex={0}
        role="region"
        aria-label="Desplazamiento del menú"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&::-webkit-scrollbar": { width: 4, backgroundColor: "transparent" },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "transparent", borderRadius: 4 },
          "&::-webkit-scrollbar-button": { display: "none" },
          "&:hover, &:focus-within, &:focus-visible": {
            scrollbarColor: `${
              isLight ? "rgba(52,71,103,0.25)" : "rgba(255,255,255,0.25)"
            } transparent`,
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isLight ? "rgba(52,71,103,0.25)" : "rgba(255,255,255,0.25)",
            },
          },
        }}
      >
        <List sx={{ px: 1, py: 1 }} aria-label="Vistas disponibles">
          {filteredRoutes.map((item) => renderItem(item))}
        </List>
      </Box>
      <Box sx={{ p: isMini ? 1 : 2, flexShrink: 0 }}>
        <Divider sx={{ mb: 2, borderColor: textColor, opacity: 0.15 }} />
        <Tooltip title={isMini ? `${userName} · ${currentUserRole}` : ""} placement="right">
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ mb: 2, justifyContent: isMini ? "center" : "flex-start" }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: "info.main", fontSize: "0.9rem" }}>
              {initials}
            </Avatar>
            {!isMini && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  title={userName}
                  sx={{ color: textColor, fontSize: "0.85rem", fontWeight: 600 }}
                >
                  {userName}
                </Typography>
                <Typography
                  noWrap
                  title={currentUserRole}
                  sx={{ color: textColor, opacity: 0.7, fontSize: "0.72rem" }}
                >
                  {currentUserRole}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
        <LogoutButton iconOnly={isMini} />
      </Box>
    </SidenavRoot>
  );
}
Sidenav.propTypes = {
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  color: PropTypes.string,
};
export default Sidenav;
